import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CloudflareImage } from '@/components/common/CloudflareImage';
import { scaleOnHover } from '@/lib/animations';
import { cn } from '@/lib/utils';
import type { Product } from '@/types';

interface ProductCardProps {
  product: Product;
  className?: string;
}

/**
 * Product card for catalog grid display
 * Features hover effects, quick view, and responsive image
 */
export function ProductCard({ product, className }: ProductCardProps) {
  const primaryImage = product.images.find((img) => img.isPrimary) || product.images[0];
  const shortDescription = product.description
    ? `${product.description.slice(0, 140)}${product.description.length > 140 ? '…' : ''}`
    : undefined;

  return (
    <motion.article
      initial="initial"
      whileHover="hover"
      variants={scaleOnHover}
      className={cn(
        'group relative bg-white overflow-hidden',
        'transition-shadow duration-300 hover:shadow-lg',
        className
      )}
    >
      <Link to={`/products/${product.slug}`} className="block">
        {/* Image Container */}
        <div className="relative aspect-[4/5] overflow-hidden bg-gray-100">
          <CloudflareImage
            src={primaryImage?.url || ''}
            alt={primaryImage?.alt || product.title}
            className="w-full h-full"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />

          {/* Hover Overlay */}
          <div
            className={cn(
              'absolute inset-0 bg-black/0 group-hover:bg-black/10',
              'transition-colors duration-300'
            )}
          />

          {/* Featured Badge */}
          {product.featured && (
            <span
              className={cn(
                'absolute top-4 left-4 px-3 py-1',
                'bg-gold text-white text-xs font-medium tracking-wide uppercase'
              )}
            >
              Öne Çıkan
            </span>
          )}
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Category */}
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">
            {product.categories[0]?.name || 'Ürün'}
          </p>

          {/* Name */}
          <h3
            className={cn(
              'font-display text-lg text-charcoal',
              'group-hover:text-gray-600 transition-colors duration-300'
            )}
          >
            {product.title}
          </h3>

          {/* Short Description */}
          {shortDescription && (
            <p className="text-sm text-gray-500 mt-2 line-clamp-2">
              {shortDescription}
            </p>
          )}

          {/* Materials */}
          {product.materials && product.materials.length > 0 && (
            <p className="text-xs text-gray-400 mt-3">
              {product.materials.slice(0, 2).join(' • ')}
            </p>
          )}
        </div>
      </Link>
    </motion.article>
  );
}
